// ════════════════════════════════════════════════════════════════════════════
// LpaField — wrapper label + hint + error com auto-conformidade WCAG
//
// Uso:
//   <LpaField label="E-mail" hint="Usado para login" error={errors.email} required>
//     <LpaInput type="email" value={form.email} onChange={...} />
//   </LpaField>
//
// Auto-injeta:
//   • <label htmlFor={id}> + id={id} no input filho
//   • aria-describedby cobrindo hint + error
//   • aria-invalid quando há erro
//   • aria-required quando required
// ════════════════════════════════════════════════════════════════════════════

import React, { cloneElement, isValidElement } from "react";
import { useFieldId } from "./helpers/useFieldId";

export interface LpaFieldProps {
  /** Label visível */
  label?: string;
  /** Hint discreto abaixo do label */
  hint?: string;
  /** Mensagem de erro inline */
  error?: string | null;
  /** Marca como obrigatório (asterisco + aria-required) */
  required?: boolean;
  /** Id explícito (default: gerado via useId) */
  id?: string;
  /** Esconde o label visualmente mas mantém para SR */
  labelSrOnly?: boolean;
  /** Conteúdo (input/select/textarea) */
  children: React.ReactNode;
  /** Layout: vertical (default) ou inline */
  layout?: "vertical" | "inline";
  className?: string;
  style?: React.CSSProperties;
}

export default function LpaField({
  label,
  hint,
  error,
  required,
  id,
  labelSrOnly,
  children,
  layout = "vertical",
  className,
  style,
}: LpaFieldProps): React.ReactElement {
  const { fieldId, hintId, errorId } = useFieldId(id);

  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;

  // Injeta id, aria-* no único filho (input/select/textarea)
  const enhancedChild = isValidElement(children)
    ? cloneElement(
        children as React.ReactElement,
        {
          id: (children.props as { id?: string }).id ?? fieldId,
          "aria-describedby": describedBy,
          "aria-invalid": Boolean(error) || undefined,
          "aria-required": required || undefined,
          invalid: Boolean(error) || (children.props as { invalid?: boolean }).invalid,
        } as Record<string, unknown>
      )
    : children;

  return (
    <div
      className={className}
      style={{
        display: layout === "inline" ? "flex" : "block",
        alignItems: layout === "inline" ? "center" : undefined,
        gap: layout === "inline" ? 12 : undefined,
        marginBottom: 14,
        ...style,
      }}
    >
      {label && (
        <label
          htmlFor={fieldId}
          style={
            labelSrOnly
              ? srOnlyStyle
              : {
                  display: "block",
                  fontFamily: "var(--lpa-font-mono)",
                  fontSize: "var(--lpa-text-2xs)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--lpa-muted)",
                  marginBottom: 4,
                }
          }
        >
          {label}
          {required && (
            <span aria-hidden="true" style={{ color: "var(--lpa-danger)", marginLeft: 4 }}>
              *
            </span>
          )}
        </label>
      )}

      {enhancedChild}

      {hint && !error && (
        <div
          id={hintId}
          style={{
            fontSize: "var(--lpa-text-xs)",
            color: "var(--lpa-muted)",
            fontFamily: "var(--lpa-font-mono)",
            marginTop: 3,
          }}
        >
          {hint}
        </div>
      )}

      {error && (
        <div
          id={errorId}
          role="alert"
          style={{
            fontSize: "var(--lpa-text-xs)",
            color: "var(--lpa-danger)",
            fontFamily: "var(--lpa-font-mono)",
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span aria-hidden="true">⚠</span>
          {error}
        </div>
      )}
    </div>
  );
}

const srOnlyStyle: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};
